import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link2, ChevronDown, ChevronRight, X, Loader2, ArrowUpCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fetchMacroLinks } from '@/lib/macroLinkBatch';
import { useProject } from '@/contexts/ProjectContext';

function buildTree(activities) {
  const map = {};
  const roots = [];
  // Group by scheduling project
  const byProject = {};
  for (const a of activities) {
    const pid = a.schedulingProjectId || '_';
    if (!byProject[pid]) byProject[pid] = { name: a.schedulingProjectName || pid, activities: [] };
    byProject[pid].activities.push(a);
    map[a.id] = { ...a, children: [] };
  }

  // Build tree per project
  const projectTrees = [];
  for (const [pid, group] of Object.entries(byProject)) {
    const projectRoots = [];
    for (const a of group.activities) {
      const node = map[a.id];
      if (a.parentId && map[a.parentId]) {
        map[a.parentId].children.push(node);
      } else {
        projectRoots.push(node);
      }
    }
    projectTrees.push({ projectId: pid, projectName: group.name, roots: projectRoots });
  }
  return projectTrees;
}

function TreeNode({ node, depth, selectedIds, onToggle, expandedIds, onToggleExpand }) {
  const isSelected = selectedIds.has(node.id);
  const isExpanded = expandedIds.has(node.id);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <>
      <div
        className="flex items-center gap-1 py-1 px-1 hover:opacity-80 cursor-pointer transition-all"
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
        onClick={() => onToggle(node)}
        data-testid={`tree-node-${node.id}`}
      >
        {hasChildren ? (
          <button onClick={e => { e.stopPropagation(); onToggleExpand(node.id); }} className="flex-shrink-0 p-0.5">
            {isExpanded
              ? <ChevronDown className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
              : <ChevronRight className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <div
          className="w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all"
          style={{
            borderColor: isSelected ? '#143A6B' : 'var(--border-color)',
            backgroundColor: isSelected ? '#143A6B' : 'transparent',
          }}
        >
          {isSelected && <Check className="w-2 h-2 text-white" />}
        </div>
        <span className="text-[10px] font-mono font-medium flex-shrink-0 px-1 rounded"
          style={{ color: '#143A6B', backgroundColor: '#143A6B08' }}>
          {node.wbsCode || '—'}
        </span>
        <span className="text-[10px] truncate flex-1 min-w-0" style={{ color: 'var(--text-primary)' }}>
          {node.name}
        </span>
        <span className="text-[9px] flex-shrink-0 font-mono" style={{ color: 'var(--text-disabled)' }}>
          {node.percentComplete || 0}%
        </span>
      </div>
      {hasChildren && isExpanded && node.children.map(child => (
        <TreeNode
          key={child.id}
          node={child}
          depth={depth + 1}
          selectedIds={selectedIds}
          onToggle={onToggle}
          expandedIds={expandedIds}
          onToggleExpand={onToggleExpand}
        />
      ))}
    </>
  );
}

export default function MacroLinkDropdown({ projectId, microModule, microActivityId, microActivityCode, microActivityName, portfolioProjectId: propPortfolioId }) {
  const { selectedProject } = useProject();
  const portfolioProjectId = propPortfolioId || selectedProject?.project_id;
  const [allActivities, setAllActivities] = useState([]);
  const [currentLinks, setCurrentLinks] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Fetch current links on mount
  useEffect(() => {
    if (!projectId || !microModule || !microActivityId) return;
    fetchMacroLinks(projectId, microModule, microActivityId)
      .then(links => {
        setCurrentLinks(links);
        setSelectedIds(new Set(links.map(l => l.macroActivityId)));
      });
  }, [projectId, microModule, microActivityId]);

  // Fetch all activities when dropdown opens
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = portfolioProjectId ? `?portfolio_project_id=${portfolioProjectId}` : '';
    api.get(`/v1/projects/${projectId}/schedule/macro/activities/search-all${params}`)
      .then(r => {
        const acts = r.data?.activities || [];
        setAllActivities(acts);
        // Auto-expand first level
        const rootIds = new Set();
        const childIds = new Set(acts.filter(a => a.parentId).map(a => a.parentId));
        childIds.forEach(id => rootIds.add(id));
        setExpandedIds(rootIds);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, projectId]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggleOpen = useCallback(() => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuWidth = 340;
      const menuHeight = 380;
      const left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8);
      const top = rect.bottom + menuHeight > window.innerHeight
        ? Math.max(rect.top - menuHeight - 4, 8)
        : rect.bottom + 4;
      setPos({ top, left: Math.max(8, left) });
    }
    setOpen(o => !o);
  }, [open]);

  const handleToggle = useCallback((node) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(node.id)) next.delete(node.id);
      else next.add(node.id);
      return next;
    });
  }, []);

  const handleToggleExpand = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Find what to add and remove
      const currentIds = new Set(currentLinks.map(l => l.macroActivityId));
      const toAdd = [...selectedIds].filter(id => !currentIds.has(id));
      const toRemove = currentLinks.filter(l => !selectedIds.has(l.macroActivityId));

      // Remove unlinked
      for (const link of toRemove) {
        if (link.linkId) {
          const tp = link.projectId || projectId;
          await api.delete(`/v1/projects/${tp}/schedule/links/${link.linkId}`);
        }
      }

      // Add new links
      for (const macroId of toAdd) {
        const macro = allActivities.find(a => a.id === macroId);
        if (!macro) continue;
        const tp = macro.schedulingProjectId || projectId;
        await api.post(`/v1/projects/${tp}/schedule/links`, {
          macroActivityId: macroId,
          microModule,
          microActivityId,
          microActivityCode,
          microActivityName,
        });
      }

      toast.success(`Updated ${selectedIds.size} macro link(s)`);
      // Refresh links
      const r = await api.get(`/v1/projects/${projectId}/schedule/${microModule}/${microActivityId}/macro-link`);
      const links = r.data?.links || (r.data?.link ? [r.data.link] : []);
      setCurrentLinks(links);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save links');
    }
    setSaving(false);
  };

  const filtered = useMemo(() => {
    if (!search) return allActivities;
    const q = search.toLowerCase();
    return allActivities.filter(a => a.name?.toLowerCase().includes(q) || a.wbsCode?.toLowerCase().includes(q));
  }, [allActivities, search]);

  const projectTrees = useMemo(() => buildTree(filtered), [filtered]);

  const linkedCount = currentLinks.length;
  const linkedLabel = linkedCount > 0
    ? currentLinks.map(l => l.macroActivityCode || l.macroActivityName?.slice(0, 8)).join(', ')
    : null;

  return (
    <div className="relative" onClick={e => e.stopPropagation()}>
      <button
        ref={btnRef}
        onClick={toggleOpen}
        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-all whitespace-nowrap max-w-[150px]"
        style={{
          background: linkedCount > 0 ? '#143A6B10' : 'var(--card-bg-alt)',
          color: linkedCount > 0 ? '#143A6B' : 'var(--text-disabled)',
          border: `1px solid ${linkedCount > 0 ? '#143A6B30' : 'var(--border-color)'}`,
        }}
        data-testid={`macro-link-dropdown-${microActivityId}`}
        title={linkedLabel ? `Linked to: ${linkedLabel}` : 'Link to Macro'}
      >
        {linkedCount > 0 ? (
          <>
            <ArrowUpCircle className="w-2.5 h-2.5 flex-shrink-0" />
            <span className="truncate">{linkedCount === 1 ? (currentLinks[0]?.macroActivityCode || '1') : `${linkedCount} linked`}</span>
          </>
        ) : (
          <>
            <Link2 className="w-2.5 h-2.5 flex-shrink-0" />
            <span>Macro</span>
            <ChevronDown className="w-2.5 h-2.5 flex-shrink-0" />
          </>
        )}
      </button>

      {open && createPortal(
        <div ref={menuRef} className="fixed w-[340px] rounded-lg shadow-2xl border z-[9999] overflow-hidden"
          style={{ top: pos.top, left: pos.left, background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
          onClick={e => e.stopPropagation()}
          data-testid={`macro-link-menu-${microActivityId}`}>

          {/* Header */}
          <div className="px-3 py-2 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
            <div className="flex items-center gap-1.5">
              <Link2 className="w-3.5 h-3.5" style={{ color: '#143A6B' }} />
              <span className="text-[11px] font-bold" style={{ color: 'var(--text-primary)' }}>Link to Macro Activities</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: '#143A6B15', color: '#143A6B' }}>
                {selectedIds.size} selected
              </span>
              <button onClick={() => setOpen(false)} style={{ color: 'var(--text-muted)' }}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="px-2 py-1.5" style={{ borderBottom: '1px solid var(--border-color)' }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or WBS code..."
              className="w-full px-2 py-1 text-[11px] rounded border"
              style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              autoFocus
              data-testid={`macro-search-${microActivityId}`}
            />
          </div>

          {/* Tree */}
          <div className="max-h-[280px] overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
            ) : projectTrees.length === 0 ? (
              <div className="text-center py-6 text-[10px]" style={{ color: 'var(--text-muted)' }}>No macro activities found</div>
            ) : (
              projectTrees.map(pt => (
                <div key={pt.projectId}>
                  {projectTrees.length > 1 && (
                    <div className="px-3 py-1 text-[9px] uppercase font-bold tracking-wider"
                      style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-disabled)', borderBottom: '1px solid var(--border-color)' }}>
                      {pt.projectName}
                    </div>
                  )}
                  {pt.roots.map(root => (
                    <TreeNode
                      key={root.id}
                      node={root}
                      depth={0}
                      selectedIds={selectedIds}
                      onToggle={handleToggle}
                      expandedIds={expandedIds}
                      onToggleExpand={handleToggleExpand}
                    />
                  ))}
                </div>
              ))
            )}
          </div>

          {/* Footer: Save */}
          <div className="px-3 py-2 flex items-center justify-between" style={{ borderTop: '1px solid var(--border-color)', backgroundColor: 'var(--hover-bg)' }}>
            <button onClick={() => setSelectedIds(new Set())} className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Clear all</button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1 rounded text-[10px] font-bold transition-all"
              style={{ background: '#143A6B', color: '#fff', opacity: saving ? 0.5 : 1 }}
              data-testid={`save-macro-links-${microActivityId}`}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Save Links
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
